/**
 * A 48px header button (carbon-shell `.h-btn`): a 20px icon in secondary
 * text that turns layer-02 with primary ink on hover, a fixed hit target
 * that never shrinks, and an optional badge kept inside its own box. With
 * `to` it is a router link. Forwards its ref and every other prop, so a
 * headless UI `Popover.Button as={HeaderButton}` works (the bell).
 */
import { forwardRef, type Ref } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import type { HeaderButtonProps } from './types'

const BASE =
  'relative grid h-12 w-12 flex-none place-items-center border-0 bg-transparent text-text-secondary ' +
  'transition-colors duration-110 ease-carbon hover:bg-layer-2 hover:text-text-primary'

export const HeaderButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, HeaderButtonProps>(function HeaderButton(
  { label, icon, badge, active = false, to, className, type = 'button', ...rest },
  ref,
) {
  const classes = clsx(BASE, active && 'bg-layer-2 text-text-primary', className)
  const body = (
    <>
      <span aria-hidden="true" className="grid place-items-center [&>svg]:h-5 [&>svg]:w-5">
        {icon}
      </span>
      {badge}
    </>
  )
  if (to) {
    return (
      <Link ref={ref as Ref<HTMLAnchorElement>} to={to} aria-label={label} className={classes}>
        {body}
      </Link>
    )
  }
  return (
    <button ref={ref as Ref<HTMLButtonElement>} type={type} aria-label={label} className={classes} {...rest}>
      {body}
    </button>
  )
})

export default HeaderButton
